import { formatCurrency } from '@mahawi/eshop-common/src/format-currency';
import { getFlag } from '@mahawi/eshop-common/src/get-flag';
import { numberCompare } from '@mahawi/eshop-common/src/number-compare';
import { stringCompare } from '@mahawi/eshop-common/src/string-compare';
import {
  type ICurrencyPrice,
  type ICurrencyType,
  type IOrderDelivery,
  type ITag,
} from '@mahawi/eshop-common/src/types';
import { Space, Tag, Typography } from 'antd';
import { Table as AntdTable } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import { ColumnFilterItem } from 'antd/es/table/interface';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { type Key, type ReactElement, useMemo } from 'react';

import { EDateFormat } from '../../fragments/date-format';
import { getDateRangeColumnFilter } from '../../fragments/date-range-column-filter';
import { EMPTY_VALUE } from '../../fragments/empty-value';
import { getCountryString } from '../../fragments/get-country-string';
import { getTagByRelativeDate } from '../../fragments/get-tag-by-relative-date';
import { getColumnSearch } from '../../fragments/table-get-column-search';
import { useCurrencyMap } from '../../hooks/use-currency-map';
import { type ICurrencyState } from '../../reducers/currency/types';
import { type IAdminOrder } from '../../reducers/orders/types';
import { type IOrdersState } from '../../reducers/orders/types';
import { type IAdminCarrierPacket } from '../../reducers/packets/types';
import { type ITagsState } from '../../reducers/tags/types';
import { SPACING } from '../../theme/tokens';
import CopyToClipboard from '../copy-to-clipboard';
import GoogleMapsButton from '../google-maps-button';
import Link from '../link';
import OpenMapButton from '../open-map-button';
import StatusTag from '../status-tag';
import Table from '../table';

dayjs.extend(relativeTime);

interface DataType extends IAdminOrder {
  key: string;
}

function OrdersTable({
  Orders,
  Currency,
  Tags,
}: {
  Orders: IOrdersState;
  Currency: ICurrencyState;
  Tags: ITagsState;
}): React.ReactElement {
  const currencyByIso = useCurrencyMap(Currency.currencies);

  const tagByColor: Map<string, ITag> = useMemo((): Map<string, ITag> => {
    const map = new Map<string, ITag>();
    for (const t of Tags.tags || []) map.set(t.color, t);
    return map;
  }, [Tags.tags]);

  // DataSource derived from Orders
  const dataSource: DataType[] = useMemo((): DataType[] => {
    if (!Orders.orders || Orders.inProcess) return [];
    return Orders.orders.map((order: IAdminOrder): DataType => ({
      ...order,
      key: order.uuid,
    }));
  }, [Orders.orders, Orders.inProcess, Orders.updatedAt]);

  const filterIsAvailable: boolean = dataSource.length > 0;

  const getFormattedPrice = (value: number, iso: string): string => {
    const currencyType: ICurrencyType | undefined = currencyByIso.get(iso);
    return currencyType
      ? formatCurrency(value, currencyType)
      : value.toString();
  };

  const columns: ColumnsType<object> = useMemo((): ColumnsType<object> => {
    const columnsUE: ColumnsType<object> = [
      {
        title: 'UUID',
        dataIndex: 'uuid',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.uuid, b.uuid),
        render: (_text: string, record: DataType): React.ReactElement => {
          const uuidSegments: string[] = record.uuid.split('-');
          const firstSegment: string = uuidSegments[0];

          return (
            <Space size={SPACING.xs} align="center">
              <Link
                to={`/order/${record.uuid}`}
                label={record.uuid}
                openInNewTab
              />
              <CopyToClipboard text={record.uuid} type="link" />
              <CopyToClipboard
                text={firstSegment}
                type="text"
                title="Copy short order UUID"
              />
            </Space>
          );
        },
        ...getColumnSearch('uuid'),
      },
      {
        title: 'Billing address',
        dataIndex: 'billingAddress',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(a.billingAddress.surname, b.billingAddress.surname),
        filterSearch: true,
        filters: filterIsAvailable
          ? [
              ...new Set(
                dataSource
                  .map((o: DataType): string =>
                    (o.billingAddress?.countryTypeIsoCode || '').toUpperCase(),
                  )
                  .filter(Boolean),
              ),
            ].map((iso: string): ColumnFilterItem => ({
              text: `${getFlag({ countryTypeIsoCode: iso })} ${iso}`,
              value: iso,
            }))
          : undefined,
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          const iso: string = (
            record.billingAddress?.countryTypeIsoCode || ''
          ).toUpperCase();
          return iso === String(value).toUpperCase();
        },
        render: (_text: string, record: DataType): React.ReactElement => {
          if (record.isForCompany) {
            return (
              <Space align="start">
                <Space orientation="vertical" size={SPACING.none}>
                  <Typography>
                    {record.billingAddress.companyName},{' '}
                    {record.billingAddress.companyId}
                  </Typography>
                  <Typography>
                    {record.billingAddress.surname} {record.billingAddress.name}
                    , {record.billingAddress.city},{' '}
                    {getCountryString(record.billingAddress.countryTypeIsoCode)}
                  </Typography>
                </Space>
                <GoogleMapsButton address={record.billingAddress} />
                <OpenMapButton address={record.billingAddress} />
              </Space>
            );
          }

          return (
            <Space align="start">
              <Typography>
                {record.billingAddress.surname} {record.billingAddress.name},{' '}
                {record.billingAddress.city},{' '}
                {getCountryString(record.billingAddress.countryTypeIsoCode)}
              </Typography>
              <GoogleMapsButton address={record.billingAddress} />
              <OpenMapButton address={record.billingAddress} />
            </Space>
          );
        },
      },
      {
        title: 'Created at',
        dataIndex: 'createdAt',
        sorter: (a: DataType, b: DataType): number =>
          dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        ...(filterIsAvailable
          ? getDateRangeColumnFilter<DataType>(
              (record: DataType): Date => record.createdAt,
            )
          : {}),
        render: (text: string): React.ReactElement => {
          const date: Date = new Date(text);

          return (
            <Space size={SPACING.none} orientation="vertical">
              <Typography>
                {dayjs(date).format(EDateFormat.DATE_TIME)}
              </Typography>
              {getTagByRelativeDate({ date })}
            </Space>
          );
        },
      },
      {
        title: 'Price without tax',
        dataIndex: 'currencyPrice',
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(
            a.currencyPrice.priceWithoutTax,
            b.currencyPrice.priceWithoutTax,
          ),
        render: (currencyPrice: ICurrencyPrice, record: DataType): string =>
          getFormattedPrice(
            currencyPrice.priceWithoutTax,
            record.currencyPrice.isoCode,
          ),
      },
      {
        title: 'Delivery fee without tax',
        dataIndex: 'orderDelivery',
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(
            a.orderDelivery.feeWithoutTax,
            b.orderDelivery.feeWithoutTax,
          ),
        render: (orderDelivery: IOrderDelivery, record: DataType): string =>
          getFormattedPrice(
            orderDelivery.feeWithoutTax,
            record.currencyPrice.isoCode,
          ),
      },
      {
        title: 'Company',
        dataIndex: 'isForCompany',
        sorter: (a: DataType, b: DataType): number =>
          numberCompare(Number(a.isForCompany), Number(b.isForCompany)),
        filterSearch: true,
        filters: filterIsAvailable
          ? [
              { text: 'Is for company', value: 'isForCompany' },
              { text: 'Is for person', value: 'isForPerson' },
              {
                text: 'Company has prices without tax',
                value: 'companyHasPricesWithoutTax',
              },
            ].map((item: ColumnFilterItem): ColumnFilterItem => ({
              text: item.text,
              value: item.value,
            }))
          : undefined,
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          if (value === 'isForCompany') {
            return record.isForCompany;
          }

          if (value === 'isForPerson') {
            return !record.isForCompany;
          }

          if (value === 'companyHasPricesWithoutTax') {
            return record.companyHasPricesWithoutTax;
          }

          return false;
        },
        render: (
          isForCompany: boolean,
          record: DataType,
        ): React.ReactElement => {
          const renderedEls: React.ReactElement[] = [];

          if (isForCompany) {
            renderedEls.push(
              <Tag key="isForCompany" color="green">
                Is for company
              </Tag>,
            );
          } else {
            renderedEls.push(
              <Tag key="isForPerson" color="blue">
                Is for person
              </Tag>,
            );
          }

          if (record.companyHasPricesWithoutTax) {
            renderedEls.push(
              <Tag key="companyHasPricesWithoutTax" color="orange">
                Company has prices without tax
              </Tag>,
            );
          }
          if (renderedEls.length === 0) {
            renderedEls.push(
              <Tag key="noTags" color="grey">
                No tags
              </Tag>,
            );
          }

          return <Space size={SPACING.xs}>{renderedEls}</Space>;
        },
      },
      {
        title: 'Review',
        dataIndex: 'review',
        sorter: (a: DataType, b: DataType): number =>
          stringCompare(
            a.review.reviewStatusType.code,
            b.review.reviewStatusType.code,
          ),
        filterSearch: true,
        filters: filterIsAvailable
          ? [
              ...new Set(
                dataSource
                  .map(
                    ({ review }: DataType): string =>
                      review.reviewStatusType.code || '',
                  )
                  .sort((a: string, b: string): number => stringCompare(a, b)),
              ),
            ].map((review: string): ColumnFilterItem => ({
              text: review,
              value: review,
            }))
          : undefined,
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.review.reviewStatusType.code === value,
        render: (_text: string, record: DataType): string => {
          if (record.review.reviewStatusType.code) {
            return record.review.reviewStatusType.code;
          }

          return EMPTY_VALUE;
        },
      },
      {
        title: 'Third party payment ID',
        dataIndex: 'thirdPartyPaymentId',
        filterSearch: true,
        filters: filterIsAvailable
          ? [
              ...new Set(
                dataSource
                  .map(
                    (o: DataType): string =>
                      o.thirdPartyPaymentIds?.join(', ') || '',
                  )
                  .filter(Boolean),
              ),
            ].map((id: string): ColumnFilterItem => ({
              text: id,
              value: id,
            }))
          : undefined,
        onFilter: (value: boolean | Key, record: DataType): boolean => {
          if (Array.isArray(record.thirdPartyPaymentIds)) {
            return record.thirdPartyPaymentIds.join(', ') === value;
          }
          return false;
        },
        render: (_text: string, record: DataType): string => {
          if (record.thirdPartyPaymentIds) {
            return record.thirdPartyPaymentIds.join(', ');
          }

          return '';
        },
      },
      {
        title: 'Shipments',
        dataIndex: 'packets',
        ...getColumnSearch('packets'),
        filterSearch: true,
        filters: filterIsAvailable
          ? [
              ...new Set(
                dataSource.flatMap((o: DataType) =>
                  o.packets.map(
                    (p: IAdminCarrierPacket): string => p.thirdPartyId,
                  ),
                ),
              ),
            ].map((id: string) => ({ text: id, value: id }))
          : undefined,
        onFilter: (value: boolean | Key, record: DataType): boolean =>
          record.packets.some(
            (p: IAdminCarrierPacket): boolean => p.thirdPartyId === value,
          ),
        render: (_: unknown, record: DataType): React.ReactElement => (
          <Space orientation="vertical" size={SPACING.xs}>
            {record.packets.map(
              (packet: IAdminCarrierPacket): React.ReactElement => {
                const latestStatus =
                  packet.CarrierPacketStatuses.length > 0
                    ? packet.CarrierPacketStatuses[
                        packet.CarrierPacketStatuses.length - 1
                      ]
                    : null;

                return (
                  <Space key={packet.id} size={SPACING.xs} align="center">
                    <Link
                      to={`/packet/${packet.id}`}
                      label={packet.thirdPartyId}
                      openInNewTab
                    />
                    {latestStatus && <StatusTag status={latestStatus.status} />}
                  </Space>
                );
              },
            )}
          </Space>
        ),
      },
      {
        title: 'Tags',
        dataIndex: 'tags',
        filterSearch: true,
        render: (
          _text: string,
          record: DataType,
        ): React.ReactElement[] | undefined => {
          if (record.tags) {
            const els: React.ReactElement[] = record.tags.map(
              (tagColor: string): React.ReactElement => {
                const found: ITag | undefined = tagByColor.get(tagColor);
                return (
                  <Tag key={tagColor} color={found?.color}>
                    {found?.name}
                  </Tag>
                );
              },
            );

            return els;
          }

          return undefined;
        },
      },
    ];

    return columnsUE;
  }, [dataSource, filterIsAvailable, tagByColor]);

  return (
    <Table
      columns={columns}
      dataSource={dataSource}
      isLoading={Orders.inProcess}
      summary={(pageData: readonly object[]): ReactElement => {
        const rows = pageData as DataType[];

        // aggregate totals per currency for the current page
        const totalsByCurrency: Map<
          string,
          { amount: number; delivery: number }
        > = rows.reduce(
          (
            acc: Map<string, { amount: number; delivery: number }>,
            r: DataType,
          ): Map<string, { amount: number; delivery: number }> => {
            const currencyIso: string = r.currencyPrice.isoCode;

            const amount: number = r.currencyPrice.priceWithoutTax;
            const delivery: number = r.orderDelivery.feeWithoutTax;

            const prev: { amount: number; delivery: number } = acc.get(
              currencyIso,
            ) || { amount: 0, delivery: 0 };
            prev.amount += amount;
            prev.delivery += delivery;
            acc.set(currencyIso, prev);

            return acc;
          },
          new Map<string, { amount: number; delivery: number }>(),
        );

        const totalsList: [string, { amount: number; delivery: number }][] =
          Array.from(totalsByCurrency.entries()).sort(
            (
              [a]: [string, { amount: number; delivery: number }],
              [b]: [string, { amount: number; delivery: number }],
            ): number => a.localeCompare(b),
          );

        const renderMultiCurrency: (
          selector: 'amount' | 'delivery',
        ) => React.ReactElement = (
          selector: 'amount' | 'delivery',
        ): React.ReactElement => {
          if (totalsList.length === 0) {
            return <Typography>0</Typography>;
          }

          return (
            <Space orientation="vertical" size={SPACING.none}>
              {totalsList.map(
                ([iso, t]: [
                  string,
                  { amount: number; delivery: number },
                ]): ReactElement => {
                  const value: number = t[selector];
                  const formatted: string = getFormattedPrice(value, iso);

                  return (
                    <Typography key={`${selector}-${iso}`}>
                      {formatted}
                    </Typography>
                  );
                },
              )}
            </Space>
          );
        };

        const countsByCountry: Map<string, number> = rows.reduce(
          (acc: Map<string, number>, r: DataType): Map<string, number> => {
            const isoCode: string = (
              r.billingAddress?.countryTypeIsoCode || ''
            ).toUpperCase();
            if (!isoCode) return acc;
            acc.set(isoCode, (acc.get(isoCode) || 0) + 1);
            return acc;
          },
          new Map<string, number>(),
        );

        const countriesList: [string, number][] = Array.from(
          countsByCountry.entries(),
        ).sort(
          (
            [isoA, countA]: [string, number],
            [isoB, countB]: [string, number],
          ): number => countB - countA || isoA.localeCompare(isoB),
        );

        const renderCountries = (): React.ReactElement => {
          if (countriesList.length === 0) {
            return <Typography>-</Typography>;
          }
          return (
            <Space orientation="vertical" size={SPACING.none}>
              {countriesList
                .sort(
                  (a: [string, number], b: [string, number]): number =>
                    b[1] - a[1],
                )
                .map(([isoCode, count]: [string, number]): ReactElement => (
                  <Typography
                    key={`country-${isoCode}`}
                  >{`${getFlag({ countryTypeIsoCode: isoCode })} ${count}`}</Typography>
                ))}
            </Space>
          );
        };

        return (
          <AntdTable.Summary fixed>
            <AntdTable.Summary.Row>
              <AntdTable.Summary.Cell index={0}>
                Summary (this page)
              </AntdTable.Summary.Cell>
              <AntdTable.Summary.Cell index={1}>
                {renderCountries()}
              </AntdTable.Summary.Cell>
              <AntdTable.Summary.Cell index={2} />
              <AntdTable.Summary.Cell index={3}>
                {renderMultiCurrency('amount')}
              </AntdTable.Summary.Cell>
              <AntdTable.Summary.Cell index={4}>
                {renderMultiCurrency('delivery')}
              </AntdTable.Summary.Cell>
              <AntdTable.Summary.Cell index={5} colSpan={columns.length - 5} />
            </AntdTable.Summary.Row>
          </AntdTable.Summary>
        );
      }}
    />
  );
}

export default OrdersTable;
